import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import type { MatchupReport, MatchupReportIndex } from "@averagejoesff/shared";
import { publishMatchupReport } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface UploadMatchupReportFormProps {
  idToken: string;
  /** Called with the server's response so the caller can update state directly. */
  onPublished: (result: { report: MatchupReport; index: MatchupReportIndex }) => void;
}

/**
 * Publishes the JSON file the superlatives skill writes each week (see
 * `docs/matchup-report.md`). Only a syntax check happens here — the api
 * Lambda does the real validation and reports every problem at once.
 */
export function UploadMatchupReportForm({ idToken, onPublished }: UploadMatchupReportFormProps) {
  const [text, setText] = useState("");
  const { status, error, submit } = useAdminForm(
    () => publishMatchupReport(parseReportText(text), idToken),
    onPublished,
  );

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setText(await file.text());
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  return (
    <form className="admin-panel__form matchup-upload" onSubmit={handleSubmit}>
      <div className="admin-panel__field">
        <label htmlFor="matchup-report-file">Report file</label>
        <input id="matchup-report-file" type="file" accept="application/json,.json" onChange={handleFile} />
      </div>
      <div className="admin-panel__field">
        <label htmlFor="matchup-report-json">…or paste the JSON</label>
        <textarea
          id="matchup-report-json"
          className="matchup-upload__json"
          rows={8}
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div className="admin-panel__actions">
        <button type="submit" disabled={status === "pending" || text.trim() === ""}>
          {status === "pending" ? "Publishing…" : "Publish report"}
        </button>
        <AdminFormStatus status={status} error={error} />
      </div>
    </form>
  );
}

/** Just enough checking to build the upload URL; the server validates the rest. */
function parseReportText(text: string): MatchupReport {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new Error(`That isn't valid JSON: ${err instanceof Error ? err.message : String(err)}`, {
      cause: err,
    });
  }
  const report = parsed as Partial<MatchupReport> | null;
  if (typeof report?.season !== "number" || typeof report.week !== "number") {
    throw new Error("The report needs a numeric season and week.");
  }
  return report as MatchupReport;
}
